import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { z } from 'zod'
import {
  locationRouteQueryOptions,
  offerDetailQueryOptions,
  useOfferDetailSuspenseQuery,
} from '@/api/accommodation/hooks'
import { ShareOfferCard } from '@/features/accommodation/share/ShareOfferCard'
import {
  SHARE_FORMATS,
  SHARE_VARIANTS,
  type ShareFormat,
  type ShareVariant,
} from '@/features/accommodation/share/types'
import { Button } from '@/components/ui/button'
import { SITE_NAME, noindexMeta } from '@/lib/seo/siteMeta'

const shareFormatKeys = Object.keys(SHARE_FORMATS) as [ShareFormat, ...ShareFormat[]]
const shareVariantKeys = SHARE_VARIANTS as unknown as [ShareVariant, ...ShareVariant[]]

const LEGACY_SHARE_VARIANTS = {
  b: 'bl',
  c: 'cl',
  e: 'el',
} as const satisfies Record<string, ShareVariant>

// Same origin the detail page's LocationCard measures the drive from.
const PRAGUE = { latitude: 50.0755, longitude: 14.4378 }

// Variants that print the driving time from Prague and need the route prefetched.
const ROUTE_VARIANTS: ShareVariant[] = ['tl', 'tr']

const cardSearchSchema = z.object({
  type: z.enum(shareFormatKeys).catch('ig-story'),
  variant: z.preprocess(
    (value) =>
      typeof value === 'string' && value in LEGACY_SHARE_VARIANTS
        ? LEGACY_SHARE_VARIANTS[value as keyof typeof LEGACY_SHARE_VARIANTS]
        : value,
    z.enum(shareVariantKeys).catch('a'),
  ),
  // Optional index into the location photos (sorted by priority): 0 = cover
  // photo, 1-5 = following photos in order. Defaults to the cover photo.
  photo: z.coerce.number().int().min(0).max(5).catch(0),
  // Number of slides the banner opens (1 = banner alone); lets a variant hint at swiping.
  carousel: z.coerce.number().int().min(1).max(10).catch(1),
})

const cardParamsSchema = z.object({
  offerId: z.string().min(1),
})

export const Route = createFileRoute('/card/offer/$offerId')({
  component: CardPage,
  errorComponent: CardErrorComponent,
  validateSearch: (search) => cardSearchSchema.parse(search),
  params: {
    parse: (params) => cardParamsSchema.parse(params),
  },
  loaderDeps: ({ search }) => ({ variant: search.variant }),
  loader: async ({ context, params, deps }) => {
    const offer = await context.queryClient.query({
      ...offerDetailQueryOptions(params.offerId),
      staleTime: 'static',
    })
    if (ROUTE_VARIANTS.includes(deps.variant)) {
      // The drive time is a nice-to-have; a routing hiccup must not cost the banner.
      await context.queryClient
        .query({
          ...locationRouteQueryOptions(offer.location.slug, PRAGUE),
          staleTime: 'static',
        })
        .catch(() => undefined)
    }
  },
  head: () => ({
    meta: [{ title: `Sdílení nabídky | ${SITE_NAME}` }, ...noindexMeta()],
  }),
})

function CardPage() {
  const { offerId } = Route.useParams()
  const { type, variant, photo, carousel } = Route.useSearch()
  const { data } = useOfferDetailSuspenseQuery(offerId)
  const pragueRoute = useQuery({
    ...locationRouteQueryOptions(data.location.slug, PRAGUE),
    enabled: ROUTE_VARIANTS.includes(variant),
  })

  const format = SHARE_FORMATS[type]

  return (
    <ShareOfferCard
      variant={variant}
      searchResult={data}
      width={format.width}
      height={format.height}
      photoIndex={photo}
      safeArea={format.safeArea}
      story={format.story}
      carousel={carousel}
      pragueRoute={pragueRoute.data}
    />
  )
}

function CardErrorComponent() {
  return (
    <div className="container flex min-h-[50vh] flex-col items-center justify-center py-16 text-center">
      <h1 className="mb-2 text-2xl font-semibold text-slate-700">Chyba při načítání karty</h1>
      <p className="mb-6 max-w-sm text-slate-500">Nabídku se nepodařilo načíst. Zkontrolujte ID nabídky v adrese.</p>
      <Button asChild>
        <Link to="/">Zpět na hlavní stránku</Link>
      </Button>
    </div>
  )
}
